/**
 * Canonical key for comparing exercise names across stored data, imports, and user input.
 * Normalizes case, hyphens/dashes, punctuation, and spacing so e.g. "Low-Bar Squat" matches
 * "Low Bar Squat".
 */
export function canonicalExerciseKey(s) {
  if (s == null || s === "") return "";
  let t = String(s).normalize("NFKC");
  t = t.replace(/[\u200B-\u200D\uFEFF]/g, ""); // zero-width / BOM
  t = t.replace(/\u00a0/g, " "); // non-breaking space → space
  t = t.toLowerCase().trim();
  // Hyphens, minus, en/em dashes → space (common in "low-bar" vs "low bar")
  t = t.replace(/[\u2013\u2014\u2212\u002d\uFE63\uFF0D]/g, " ");
  t = t.replace(/[''`´]/g, "");
  // Non letters/digits → space (keeps unicode letters via \p{L} if supported)
  try {
    t = t.replace(/[^\p{L}\p{N}]+/gu, " ");
  } catch {
    t = t.replace(/[^a-z0-9]+/gi, " ");
  }
  t = t.replace(/\s+/g, " ").trim();
  return t;
}

/**
 * True if two exercise names refer to the same movement for UI purposes:
 * exact canonical match, substring (long enough), word Jaccard, or all words
 * of the shorter name appear in the longer (when shorter has 2+ words).
 */
export function exerciseNamesMatch(a, b) {
  const ca = canonicalExerciseKey(a);
  const cb = canonicalExerciseKey(b);
  if (!ca || !cb) return false;
  if (ca === cb) return true;

  const minSub = 5;
  if (
    ca.length >= minSub &&
    cb.length >= minSub &&
    (ca.includes(cb) || cb.includes(ca))
  ) {
    return true;
  }

  const wa = ca.split(" ").filter(Boolean);
  const wb = cb.split(" ").filter(Boolean);
  if (wa.length === 0 || wb.length === 0) return false;

  const setA = new Set(wa);
  const setB = new Set(wb);
  let inter = 0;
  for (const w of setA) {
    if (setB.has(w)) inter += 1;
  }
  const union = new Set([...wa, ...wb]).size;
  if (union > 0 && inter / union >= 0.5) return true;

  const [smaller, larger] = wa.length <= wb.length ? [setA, setB] : [setB, setA];
  if (smaller.size >= 2) {
    for (const w of smaller) {
      if (!larger.has(w)) return false;
    }
    return true;
  }

  return false;
}

export function canonicalNameSetFromStrings(names) {
  const set = new Set();
  if (!Array.isArray(names)) return set;
  for (const n of names) {
    const k = canonicalExerciseKey(n);
    if (k) set.add(k);
  }
  return set;
}

/** Collect typed exercise names from react-final-form workout values. */
export function collectExerciseNamesFromFormExercises(exercises) {
  const names = [];
  if (!Array.isArray(exercises)) return names;
  for (const ex of exercises) {
    if (!ex) continue;
    const t = String(ex.exerciseType || "single").toLowerCase();
    const hasCircuitData =
      Array.isArray(ex.circuit) &&
      ex.circuit.some((r) => Array.isArray(r) && r.length > 0);
    if (t === "circuit" || hasCircuitData) {
      if (Array.isArray(ex.circuit)) {
        for (const round of ex.circuit) {
          if (!Array.isArray(round)) continue;
          for (const sub of round) {
            const n = sub?.name;
            if (n != null && String(n).trim()) names.push(String(n).trim());
          }
        }
      }
      const parent = ex.name;
      if (parent != null && String(parent).trim()) names.push(String(parent).trim());
    } else {
      const n = ex.name;
      if (n != null && String(n).trim()) names.push(String(n).trim());
    }
  }
  return names;
}
